"use client";

import { useMemo, useEffect, useCallback, useRef, useState } from 'react'
import { getAuthToken, getRealtimeAuthToken } from '@/shared/lib/auth-token'
import { useShallow } from 'zustand/react/shallow'
import { useTranslation } from 'react-i18next'
import { useIdleTimer } from '@/shared/hooks/use-idle-timer'
import { useAutoAgent } from '@/entities/agent/api/hooks'
import { useAgentConnection, AgentConnectionManager } from '@/entities/realtime-agent/model/use-agent-connection'
import { useAgentMessages } from '@/entities/realtime-agent/model/use-agent-messages'
import {
  avatarSetupCoordinator,
  type AvatarSetupBinding,
} from '@/entities/realtime-agent/model/avatar-setup-coordinator'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { useAvatarStore } from '@/shared/store/avatar-store'
import { useVadStore } from '@/shared/store/vad'
import { useTextChat } from '@/features/text-chat-with-agent/model/use-text-chat'
import { useVoiceChatVAD } from '@/features/voice-chat-with-agent/model/vad'
import { useChatModeController } from '@/features/avatar/manage-chat-modes/model/use-chat-mode-controller'
import { useConsentController } from '@/features/avatar/manage-consent/model/use-consent-controller'
import { useMediaController } from '@/features/avatar/manage-media-state/model/use-media-controller'
import { useVADController } from '@/features/avatar/manage-vad-state/model/use-vad-controller'
import { deepMerge, useOptimizedMessages, useOptimizedLoading, mergeAvatarConfig } from '../lib/config-utils'
import { transformPageStyleToAvatarConfig, extractPageConfigFromAgent } from '../lib/config-transformer'
import { useAvatarLoadingManager } from './use-avatar-loading-manager'
import { useVADSynchronizer } from './use-vad-synchronizer'
import { useMessageHandler } from './use-message-handler'
import { useAgentDataLogger } from './use-agent-data-logger'
import { useDisconnectionHandler } from './use-disconnection-handler'
import { useSearchChat } from '@/features/search-chat'
import { useVoiceChatAudioPlayer } from '@/features/voice-chat-with-agent/model/audio-player'
import { shouldPreplayGreetingEntrance } from '@/features/voice-chat-with-agent/model/audio-start-animation'
import { buildAvatarConfigFromAgent } from '@/entities/realtime-agent/lib/avatar-config-builder'
import { useSttToast, useTtsToast, useConnectionToast } from '../toast/hooks'
import { getCustomAvatar, getCustomAvatarPublic } from '@/app/actions/custom-avatar'
import { getScenarioConfigs, getAnimations, getScenarioConfigsPublic, getAnimationsPublic } from '@/app/actions/asset-library'
import { setCustomAvatarData } from '@/entities/avatar-scenario/const/config'
import { transformToCustomAvatarData, transformToCustomAvatarDataWithScenarios } from '@/shared/lib/threejs/config/custom-avatar.config'
import { usePathname } from 'next/navigation'
import { isPublicPath } from '@/shared/hooks/use-optional-auth'
import { generateUUID } from '@/shared/utils/uuid'
import type { AvatarPageConfig } from '../types/avatar-page-config'
import type { Messages } from '../types/message'
import type { Agent } from '@/entities/agent/types/types'
import type { LanguageCode } from '@/entities/agent-preset/model/types'
import type { AvatarType } from '@/entities/avatar-scenario/const/config'
import type { CustomAvatar, DisplayMode } from '@/features/custom-avatar/types'

interface UseCustomAvatarPageParams {
  agentId: string
  displayMode?: DisplayMode
  config?: Partial<AvatarPageConfig>
  onModeChange?: (payload: { fromMode: 'voice' | 'chat'; toMode: 'voice' | 'chat' }) => void
  onConsentConfirm?: () => void
  onMediaStateChange?: (state: { isSpeakerOn: boolean; isMicOn: boolean }) => void
  onVADStatusChange?: (state: { status: string; reason: string }) => void
  /** 유휴 타이머 활성화 여부 (기본: false) */
  enableIdleTimer?: boolean
  /** 유휴 타임아웃 시간 (밀리초) - 기본 60초 (웹용) */
  idleTimeoutMs?: number
  /** 타이머 경고 시작 시점 (남은 밀리초) - 기본 10초 */
  timerWarningMs?: number
}

export interface UseCustomAvatarPageReturn {
  mergedConfig: AvatarPageConfig
  chatModeController: ReturnType<typeof useChatModeController>
  consentController: ReturnType<typeof useConsentController>
  mediaController: ReturnType<typeof useMediaController>
  vadController: ReturnType<typeof useVADController>
  messages: Messages
  isLoading: boolean
  pitch: number
  handleSendMessage: (message: string) => void
  handleChatModeToggle: () => void
  handleNavigateToMode: (mode: "call" | "text" | null) => void
  handleBackClick: () => void
  pauseAudio: () => void
  // Agent data
  agentData: Agent | undefined
  isAgentLoading: boolean
  avatarType: AvatarType
  // Custom avatar
  customAvatar: CustomAvatar | null
  isCustomAvatarLoaded: boolean
  // Reconnect modal
  showReconnectModal: boolean
  disconnectReason: string | null
  handleReconnect: () => Promise<void>
  // Search Chat (Backend Realtime API)
  isSearchEnabled: boolean
  searchState: 'idle' | 'searching' | 'completed'
  searchQuery: string | null
  handleSearchToggle: (enabled: boolean) => void
  showSearchToggle: boolean
  // Idle Timer (웹 모드용)
  isTimerVisible: boolean
  remainingSeconds: number
  resetIdleTimer: () => void
}

/**
 * Custom Avatar Page 메인 비즈니스 로직 Hook
 *
 * 책임:
 * - 모든 상태 및 로직 통합
 * - UI 컴포넌트에 필요한 props 제공
 * - Feature controllers 조율
 */
export const useCustomAvatarPage = ({
  agentId,
  displayMode = 'web',
  config,
  onModeChange,
  onConsentConfirm,
  onMediaStateChange,
  onVADStatusChange,
  enableIdleTimer = false,
  idleTimeoutMs = 10000,  // 웹 기본값: 10초
  timerWarningMs = 10000,
}: UseCustomAvatarPageParams): UseCustomAvatarPageReturn => {
  // ==================== Route Detection ====================
  const pathname = usePathname()
  const isPublicRoute = isPublicPath(pathname)

  // ==================== i18n ====================
  const { i18n } = useTranslation()
  const rawLanguage = (i18n.language || 'ko').split('-')[0]
  const currentLanguage = (
    ['ko', 'en', 'zh', 'ja'].includes(rawLanguage) ? rawLanguage : 'ko'
  ) as LanguageCode

  // ==================== Agent 데이터 ====================
  // staleTime: 0과 refetchOnMount: 'always'로 항상 최신 데이터 로드
  // useAutoAgent: 공개 경로(/{slug}, /expo/*)에서는 인증 없이 동작
  const { data: agentData, isLoading: isAgentLoading } = useAutoAgent(agentId, {
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Greeting 재생 상태 추적 (useGreeting 패턴)
  const hasPlayedGreetingRef = useRef(false)
  // 텍스트 채팅 인사용 ref (콜백에서 안전하게 접근)
  const sendGreetingRef = useRef<(() => void) | null>(null)

  // 커스텀 아바타 상태
  const [customAvatar, setCustomAvatar] = useState<CustomAvatar | null>(null)
  const [isCustomAvatarLoaded, setIsCustomAvatarLoaded] = useState(false)
  const settingsVersion = useAvatarStore(state => state.settingsVersion)

  // 🔧 이전 avatarType 추적 - 타이밍 이슈 방지
  // avatarType이 변경되면 로딩 상태를 강제로 표시
  const prevAvatarTypeRef = useRef<AvatarType | null>(null)

  // 아바타 타입 추출 (agent.config.avatar_type)
  const avatarType: AvatarType = useMemo(() => {
    const type = agentData?.config?.avatar_type as AvatarType | undefined
    const validTypes: AvatarType[] = ['glyx', 'nalnani', 'kikiz', 'bambam', 'vroid', 'goesan', 'gangwha', 'bobbi', 'taekwondo', 'custom']
    if (type && validTypes.includes(type)) {
      return type
    }
    return 'nalnani' // 기본값
  }, [agentData?.config?.avatar_type])

  // 🔧 avatarType이 'custom'으로 변경되었는지 감지 (타이밍 이슈 방지)
  // 이 값은 동기적으로 계산되므로 state 업데이트 지연 문제를 우회함
  const justChangedToCustom = avatarType === 'custom' && prevAvatarTypeRef.current !== 'custom'

  // 🔧 effectiveIsCustomAvatarLoaded: 실제 로딩 완료 상태
  // justChangedToCustom이 true면 아직 로딩 중으로 간주
  const effectiveIsCustomAvatarLoaded = isCustomAvatarLoaded && !justChangedToCustom

  // 🔧 렌더링 끝에 ref 업데이트 (다음 렌더에서 사용됨)
  // 이 코드는 컴포넌트 끝에서 실행되어야 하지만, 값은 위에서 이미 계산됨
  prevAvatarTypeRef.current = avatarType

  // 커스텀 아바타 로드 및 Three.js config 설정
  useEffect(() => {
    // 🔧 동기적으로 먼저 로딩 상태 리셋 (avatarType이 custom인 경우)
    // 이렇게 해야 React 배치 업데이트로 인한 타이밍 이슈를 방지함
    if (avatarType === 'custom') {
      setIsCustomAvatarLoaded(false)
    }

    const loadCustomAvatarData = async () => {
      if (avatarType !== 'custom') {
        // 커스텀이 아니면 null로 초기화
        setCustomAvatarData(null)
        setIsCustomAvatarLoaded(true)
        return
      }

      // 위에서 이미 동기적으로 리셋됨

      try {
        // 공개 경로에서는 인증 불필요한 공개 API 사용
        const avatar = isPublicRoute
          ? await getCustomAvatarPublic(agentId)
          : await getCustomAvatar(agentId)

        if (avatar) {
          setCustomAvatar(avatar)

          // 모드별 설정 선택 (kiosk 모드면 kiosk 전용 설정 우선, 없으면 web 설정으로 fallback)
          const resolvedCamera = displayMode === 'kiosk'
            ? (avatar.kioskCameraConfig || avatar.cameraConfig)
            : avatar.cameraConfig
          const resolvedLighting = displayMode === 'kiosk'
            ? (avatar.kioskLightingConfig || avatar.lightingConfig)
            : avatar.lightingConfig
          const resolvedLipsync = displayMode === 'kiosk'
            ? (avatar.kioskLipsyncConfig || avatar.lipsyncConfig)
            : avatar.lipsyncConfig
          const resolvedAvatarTransform = displayMode === 'kiosk'
            ? (avatar.kioskAvatarTransform || avatar.avatarTransform)
            : avatar.avatarTransform

          // 시나리오 설정 및 애니메이션 라이브러리 로드 (병렬)
          // 공개 경로에서는 인증 불필요한 공개 API 사용
          const fetchScenarios = isPublicRoute ? getScenarioConfigsPublic : getScenarioConfigs
          const [scenarioConfigs, animationLibrary] = await Promise.all([
            fetchScenarios(avatar.id).catch((err) => {
              console.error('🎭 [CUSTOM-AVATAR] 시나리오 설정 로드 실패:', err)
              console.error('🎭 [CUSTOM-AVATAR] avatar.id:', avatar.id)
              return []
            }),
            (isPublicRoute
              ? getAnimationsPublic(avatar.bodyType, agentId)
              : getAnimations(avatar.bodyType)
            ).catch((err) => {
              console.warn('🎭 [CUSTOM-AVATAR] 애니메이션 라이브러리 로드 실패:', err)
              return []
            }),
          ])

          // 상세 로깅
          console.log('🎭 [CUSTOM-AVATAR] 시나리오 로드 결과:', {
            avatarId: avatar.id,
            scenarioConfigsCount: scenarioConfigs.length,
            animationLibraryCount: animationLibrary.length,
            scenarioTypes: scenarioConfigs.map(c => c.scenarioType),
          })

          if (scenarioConfigs.length === 0) {
            console.warn('🎭 [CUSTOM-AVATAR] 시나리오 설정이 비어있습니다. DB 확인 필요:', avatar.id)
          }

          // Three.js config용 데이터 변환 및 설정
          // 시나리오 설정이 있으면 확장 버전 사용, 없으면 레거시 버전 사용
          const customAvatarData = scenarioConfigs.length > 0
            ? transformToCustomAvatarDataWithScenarios(
                {
                  id: avatar.id,
                  name: avatar.name,
                  bodyType: avatar.bodyType,
                  modelUrl: avatar.modelUrl,
                  thumbnailUrl: avatar.thumbnailUrl,
                  cameraConfig: resolvedCamera as unknown as Record<string, unknown>,
                  lightingConfig: resolvedLighting as unknown as Record<string, unknown>,
                  lipsyncConfig: resolvedLipsync as unknown as Record<string, unknown>,
                  animations: avatar.animations.map(anim => ({
                    id: anim.id,
                    name: anim.name,
                    scenarioType: anim.scenarioType,
                    animationUrl: anim.animationUrl,
                    playMode: anim.playMode,
                    blendDuration: anim.blendDuration,
                  })),
                  avatarTransform: resolvedAvatarTransform,
                },
                scenarioConfigs.map(config => ({
                  id: config.id,
                  scenarioType: config.scenarioType,
                  animations: config.animations.map(anim => ({
                    animationId: anim.animationId,
                    isDefault: anim.isDefault,
                    order: anim.order,
                  })),
                  playMode: config.playMode,
                  blendDuration: config.blendDuration,
                })),
                animationLibrary.map(anim => ({
                  id: anim.id,
                  name: anim.name,
                  bodyType: anim.bodyType,
                  animationUrl: anim.animationUrl,
                }))
              )
            : transformToCustomAvatarData({
                id: avatar.id,
                name: avatar.name,
                bodyType: avatar.bodyType,
                modelUrl: avatar.modelUrl,
                thumbnailUrl: avatar.thumbnailUrl,
                cameraConfig: resolvedCamera as unknown as Record<string, unknown>,
                lightingConfig: resolvedLighting as unknown as Record<string, unknown>,
                lipsyncConfig: resolvedLipsync as unknown as Record<string, unknown>,
                animations: avatar.animations.map(anim => ({
                  id: anim.id,
                  name: anim.name,
                  scenarioType: anim.scenarioType,
                  animationUrl: anim.animationUrl,
                  playMode: anim.playMode,
                  blendDuration: anim.blendDuration,
                })),
                avatarTransform: resolvedAvatarTransform,
              })

          console.log('🎭 [CUSTOM-AVATAR] setCustomAvatarData 호출 전, data:', customAvatarData)
          console.log('🎭 [CUSTOM-AVATAR] modelUrl:', customAvatarData.modelUrl)
          console.log('🎭 [CUSTOM-AVATAR] 시나리오 설정 적용:', scenarioConfigs.length > 0 ? '시나리오 설정 기반' : '레거시 애니메이션 기반')
          setCustomAvatarData(customAvatarData)
          console.log('🎭 [CUSTOM-AVATAR] Three.js config 설정 완료')
        } else {
          console.warn('🎭 [CUSTOM-AVATAR] 커스텀 아바타를 찾을 수 없음')
          setCustomAvatarData(null)
        }
      } catch (error) {
        console.error('🎭 [CUSTOM-AVATAR] 로드 실패:', error)
        setCustomAvatarData(null)
      } finally {
        setIsCustomAvatarLoaded(true)
      }
    }

    if (avatarType) {
      loadCustomAvatarData()
    }
  }, [avatarType, agentId, displayMode, settingsVersion, isPublicRoute])

  // ==================== DB Config 추출 ====================
  const configFromAgent = useMemo(() => {
    if (!agentData?.config) {
      console.log('📄 [PAGE-CONFIG] agentData.config is empty');
      return {};
    }

    const { pageStyle, intro } = extractPageConfigFromAgent(agentData.config);

    const transformed = transformPageStyleToAvatarConfig(pageStyle, intro);

    return transformed;
  }, [agentData?.config]);

  // ==================== Config 병합 ====================
  // 우선순위: props config > agent DB config > defaults
  const mergedConfig = useMemo(() => {
    const baseConfig = mergeAvatarConfig({
      ...configFromAgent,  // DB에서 불러온 설정
      ...config,           // props로 전달된 설정 (프리뷰용)
    });
    return baseConfig;
  }, [config, configFromAgent])

  // ==================== WebSocket & 음성 ====================
  const { sendMessage: sendRealtimeMessage } = useAgentMessages()
  const { pitch } = useVoiceChatVAD()

  // WebSocket 연결 상태 구독
  const isConnected = useRealtimeAgentStore((state) => state.isConnected)
  const isAvatarSetupComplete = useRealtimeAgentStore((state) => state.isAvatarSetupComplete)
  const avatarSetupRevision = useRealtimeAgentStore((state) => state.avatarSetupRevision)
  const hasConsented = useRealtimeAgentStore((state) => state.hasConsented)
  const isRealtimeLoadingForTimer = useRealtimeAgentStore((state) => state.isLoading)  // 타이머용
  const isGreetingLoading = useRealtimeAgentStore((state) => state.isGreetingLoading)
  const messagesForTimer = useRealtimeAgentStore((state) => state.messages)  // 타이머 - 사용자 메시지 감지용
  const isAvatarReady = useAvatarStore((state) => state.isAvatarReady)
  const currentAnimation = useAvatarStore((state) => state.currentAnimation)
  const vadStatus = useVadStore((state) => state.vadStatus)  // VAD 상태 (listening 감지용)
  const prevVadStatusRef = useRef(vadStatus)

  // avatarConfig 구독 (avatar_setup effect에서 필요)
  const avatarConfig = useRealtimeAgentStore((state) => state.avatarConfig)

  const avatarSetupBinding = useMemo<AvatarSetupBinding | null>(() => {
    if (!agentData?.id || !avatarConfig?.avatar_id) return null
    const voiceId = currentLanguage === 'en'
      ? avatarConfig.english_voice_id
      : currentLanguage === 'zh'
        ? avatarConfig.chinese_voice_id ?? avatarConfig.korean_voice_id
        : currentLanguage === 'ja'
          ? avatarConfig.japanese_voice_id ?? avatarConfig.korean_voice_id
          : avatarConfig.korean_voice_id

    return {
      agentId: agentData.id,
      language: currentLanguage,
      realtimeProvider: agentData.realtime_provider ?? 'none',
      avatarId: avatarConfig.avatar_id,
      avatarType: avatarConfig.avatar_type,
      voiceId,
    }
  }, [agentData?.id, agentData?.realtime_provider, avatarConfig, currentLanguage])

  useAgentConnection(avatarSetupBinding)

  const avatarSetupIdentity = avatarSetupBinding
    ? JSON.stringify(avatarSetupBinding)
    : null
  const previousAvatarSetupIdentityRef = useRef<string | null>(null)

  useEffect(() => {
    const previousIdentity = previousAvatarSetupIdentityRef.current
    previousAvatarSetupIdentityRef.current = avatarSetupIdentity
    if (!previousIdentity || !avatarSetupIdentity || previousIdentity === avatarSetupIdentity) return

    const api = AgentConnectionManager.getAPI()
    if (api) avatarSetupCoordinator.invalidate(api)
    useRealtimeAgentStore.getState().invalidateAvatarSetup()
  }, [avatarSetupIdentity])

  // ==================== AvatarConfig 설정 ====================
  // 참고: agentId는 부모 페이지 (slug/page.tsx, chat/page.tsx)에서 이미 설정함
  // 여기서 중복 호출하면 타이밍 문제 발생 가능
  useEffect(() => {
    if (agentData) {
      const { avatarConfig } = buildAvatarConfigFromAgent(agentData)
      console.log('📷 [CUSTOM-AVATAR-PAGE] avatarConfig 설정:', avatarConfig)
      useRealtimeAgentStore.getState().setAvatarConfig(avatarConfig)
      // realtimeProvider 값도 Store에 저장 (연결 시 avatar_setup에서 사용)
      const realtimeProvider = agentData.realtime_provider ?? 'none'
      console.log('📷 [CUSTOM-AVATAR-PAGE] realtimeProvider 설정:', realtimeProvider)
      useRealtimeAgentStore.getState().setRealtimeProvider(realtimeProvider)
      // displayMode 값도 Store에 저장 (메시지 전송 시 Backend로 전달)
      console.log('📷 [CUSTOM-AVATAR-PAGE] displayMode 설정:', displayMode)
      useRealtimeAgentStore.getState().setDisplayMode(displayMode)
    }
  }, [agentData, displayMode])

  // ==================== Avatar Setup 전송 (연결 + config 모두 준비되면) ====================
  useEffect(() => {
    console.log('📷 [AVATAR-SETUP-EFFECT] 체크:', {
      isConnected,
      isAvatarSetupComplete,
      hasAvatarConfig: !!avatarConfig,
      agentId: agentData?.id,
      avatar_id: avatarConfig?.avatar_id,
      korean_voice_id: avatarConfig?.korean_voice_id,
    })

    // 이미 setup 완료되었으면 스킵
    if (isAvatarSetupComplete) {
      console.log('📷 [AVATAR-SETUP-EFFECT] 이미 완료됨 - 스킵')
      return
    }

    if (!isConnected || !agentData?.id || !avatarConfig || !avatarSetupBinding) return

    const api = AgentConnectionManager.getAPI()
    if (!api?.isConnected) {
      console.log('📷 [AVATAR-SETUP-EFFECT] API 연결 안됨')
      return
    }

    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    const realtimeProvider = agentData.realtime_provider ?? 'none'

    const attemptSetup = async () => {
      const result = await avatarSetupCoordinator.request({
        api,
        binding: avatarSetupBinding,
        requiresToken: realtimeProvider === 'openai',
        fetchToken: () => getRealtimeAuthToken({
          agentId: agentData.id,
          realtimeProvider,
        }),
        sendSetup: (token) => {
          if (cancelled || !api.isConnected) return false
          // 공개 OpenAI Realtime은 단기 토큰 없이는 setup을 보내지 않는다.
          if (realtimeProvider === 'openai' && !token) return false
          return api.sendAvatarSetup(
            agentData.id,
            avatarConfig,
            token,
            realtimeProvider,
            currentLanguage,
          )
        },
      })

      if (cancelled) return
      if (
        result.status === 'retryable'
        && avatarSetupCoordinator.canRetry(api, avatarSetupBinding)
      ) {
        const retryDelayMs = Math.min(2000, 500 * result.attempts)
        retryTimer = setTimeout(attemptSetup, retryDelayMs)
      } else if (result.status === 'exhausted') {
        console.error('❌ [AVATAR-SETUP-EFFECT] 토큰/setup 재시도 횟수 초과')
      }
    }

    void attemptSetup()
    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [
    isConnected,
    isAvatarSetupComplete,
    avatarSetupRevision,
    agentData?.id,
    agentData?.realtime_provider,
    avatarConfig,
    avatarSetupBinding,
    currentLanguage,
  ])

  // ==================== 오디오 플레이어 ====================
  const { resetAudioPlayer } = useVoiceChatAudioPlayer()

  const pauseAudio = useCallback(() => {
    resetAudioPlayer()
  }, [resetAudioPlayer])

  // ==================== Callbacks ====================
  const handleModeChange = useCallback((payload: { fromMode: 'voice' | 'chat'; toMode: 'voice' | 'chat' }) => {
    console.log('🔄 모드 전환:', payload.fromMode, '→', payload.toMode)
    onModeChange?.(payload)
  }, [onModeChange])

  const handleConsentConfirm = useCallback(() => {
    console.log('✅ 동의 완료 - 컨트롤러에서 처리됨')
    onConsentConfirm?.()
  }, [onConsentConfirm])

  const handleMediaStateChange = useCallback((state: { isSpeakerOn: boolean; isMicOn: boolean }) => {
    console.log('🔊 미디어 상태 변경:', state)
    onMediaStateChange?.(state)
  }, [onMediaStateChange])

  const handleVADStatusChange = useCallback((state: { status: string; reason: string }) => {
    console.log('🎙️ VAD 상태 변경:', state.reason, '→', state.status)
    onVADStatusChange?.(state)
  }, [onVADStatusChange])

  // ==================== Feature Controllers ====================
  const chatModeController = useChatModeController()

  const consentController = useConsentController({
    onConsentGranted: handleConsentConfirm,
    onConsentDeclined: () => {
      chatModeController.switchToChatMode()
      // 텍스트 채팅 모드 진입 시 인사 메시지 전송 (사용자 메시지 미표시)
      setTimeout(() => {
        sendGreetingRef.current?.()
      }, 500)
    }
  })

  const mediaController = useMediaController()

  const vadController = useVADController({
    onStatusChange: handleVADStatusChange,
  })

  // ==================== Toast Hooks ====================
  // STT/TTS/연결상태 토스트 (음성 모드에서만 활성화)
  useSttToast(chatModeController.currentMode)
  useTtsToast(chatModeController.currentMode)
  useConnectionToast()

  // ==================== Reconnect Modal 상태 ====================
  const showReconnectModal = useRealtimeAgentStore(state => state.showReconnectModal)
  const disconnectReason = useRealtimeAgentStore(state => state.disconnectReason)

  // ==================== Disconnection Handler ====================
  const { handleReconnect } = useDisconnectionHandler({
    consentController,
    chatModeController,
    pauseAudio,
  })

  // ==================== 자동 재연결 (웹 모드) ====================
  // disconnect_notice 수신 또는 웹소켓 끊김 시 즉시 자동 재연결
  useEffect(() => {
    if (showReconnectModal) {
      console.log('🔄 [WEB] 웹소켓 끊김 감지 - 즉시 자동 재연결')
      handleReconnect()
        .then(() => {
          console.log('✅ [WEB] 재연결 성공 - ConsentModal로 복귀')
        })
        .catch((error) => {
          console.error('❌ [WEB] 자동 재연결 실패:', error)
        })
    }
  }, [showReconnectModal, handleReconnect])

  // ==================== 세션 종료 시 DB 저장 ====================
  // Redis 버퍼의 메시지를 DB에 저장 (Analytics용)
  const flushSessionToDb = useCallback(async () => {
    const sessionId = useRealtimeAgentStore.getState().sessionId
    if (!sessionId) {
      console.log('📤 [FLUSH] sessionId 없음 - 스킵')
      return
    }

    try {
      console.log('📤 [FLUSH] 세션 메시지 DB 저장 시작:', sessionId)
      const token = await getAuthToken()

      const response = await fetch(`/api/realtime/${sessionId}/flush`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      })

      if (response.ok) {
        const result = await response.json()
        console.log('✅ [FLUSH] DB 저장 완료:', result)
      } else {
        console.warn('⚠️ [FLUSH] DB 저장 실패:', response.status)
      }
    } catch (error) {
      console.error('❌ [FLUSH] DB 저장 에러:', error)
    }
  }, [])

  // ==================== 유휴 타이머 (웹 모드용) ====================
  // 세션 리셋 함수 (타임아웃 시 호출)
  const resetSessionForTimeout = useCallback(async () => {
    console.log('⏰ [WEB] 유휴 타임아웃 - 세션 리셋')

    // 0. 세션 메시지 DB 저장 (Analytics용)
    await flushSessionToDb()

    // 1. 오디오 정지
    pauseAudio()

    // 2. 현재 턴 정리
    useRealtimeAgentStore.getState().clearCurrentTurn()

    // 3. 메시지 초기화
    useRealtimeAgentStore.getState().setMessages([])

    // 4. Greeting ref 리셋
    hasPlayedGreetingRef.current = false

    // 5. 동의 상태 리셋 (ConsentModal 다시 표시)
    consentController.resetConsent()

    // 6. 채팅 모드 초기화
    chatModeController.switchToVoiceMode()

    console.log('✅ [WEB] 타임아웃 리셋 완료 - ConsentModal로 복귀')
  }, [flushSessionToDb, pauseAudio, consentController, chatModeController])

  // 🔧 디버그: 타이머 조건 값 추적 (빌드 버전: 2026-03-03-v1)
  const isPausedValue = currentAnimation === 'speaking' || isRealtimeLoadingForTimer
  console.log('🔍 [WEB-TIMER-v3] BUILD: 2026-03-03-21:45')
  console.log('🔍 [WEB-TIMER-v3] enableIdleTimer:', enableIdleTimer, 'hasConsented:', hasConsented)
  console.log('🔍 [WEB-TIMER-v3] isPaused:', isPausedValue, '(speaking:', currentAnimation === 'speaking', ', loading:', isRealtimeLoadingForTimer, ')')

  // 유휴 타이머 Hook (단순화: hasConsented만 사용)
  // 키오스크와 달리 웹은 hasVadStarted 없이 동의 즉시 타이머 활성화
  const idleTimer = useIdleTimer({
    idleTimeoutMs,
    warningMs: timerWarningMs,
    enabled: enableIdleTimer && hasConsented,  // 🔧 hasVadStarted 제거 - 타이밍 이슈 해결
    isPaused: isPausedValue,
    onTimeout: resetSessionForTimeout,
    debugPrefix: 'WEB',
  })

  // 동의 완료 시 타이머 즉시 시작
  useEffect(() => {
    console.log('🔍 [WEB-TIMER-EFFECT-v2] 조건 체크 - enableIdleTimer:', enableIdleTimer, 'hasConsented:', hasConsented, 'BUILD: 2026-02-25-20:30')
    if (!enableIdleTimer || !hasConsented) return

    console.log('✅ [WEB-v2] 동의 완료 - 타이머 즉시 시작')
    idleTimer.startTimer()
  }, [enableIdleTimer, hasConsented, idleTimer])

  // listening 상태 진입 시 타이머 재시작 (사용자 활동 감지)
  useEffect(() => {
    if (!enableIdleTimer || !hasConsented) return

    const prevStatus = prevVadStatusRef.current
    prevVadStatusRef.current = vadStatus

    // listening 진입 감지 → 타이머 재시작
    if (vadStatus === 'listening' && prevStatus !== 'listening') {
      console.log(`🎤 [WEB] listening 진입 (from: ${prevStatus}) - 타이머 재시작`)
      idleTimer.startTimer()
    }
  }, [enableIdleTimer, vadStatus, hasConsented, idleTimer])

  // 사용자 메시지(STT) 감지: 타이머 정지
  const prevMessagesLenRef = useRef(messagesForTimer.length)
  useEffect(() => {
    if (!enableIdleTimer || !hasConsented) return

    if (messagesForTimer.length > prevMessagesLenRef.current) {
      const lastMsg = messagesForTimer[messagesForTimer.length - 1]
      if (lastMsg?.sender === 'user') {
        console.log('💬 [WEB] 사용자 메시지 감지 - 타이머 정지')
        idleTimer.stopTimer()
      }
    }
    prevMessagesLenRef.current = messagesForTimer.length
  }, [enableIdleTimer, hasConsented, messagesForTimer, idleTimer])

  // ==================== Search Chat (Backend Realtime API) ====================
  // Zustand Store에서 검색 관련 상태 구독
  const chatBackend = useRealtimeAgentStore((state) => state.chatBackend)
  const isSearchEnabled = useRealtimeAgentStore((state) => state.isSearchEnabled)
  const searchState = useRealtimeAgentStore((state) => state.searchState)
  const searchQuery = useRealtimeAgentStore((state) => state.searchQuery)
  const setChatBackend = useRealtimeAgentStore((state) => state.setChatBackend)

  // avatarId 추출 (avatarConfig에서 또는 기본값)
  const avatarId = avatarConfig?.avatar_id || 'default'

  // Clerk 인증 토큰 (auth routes에서만 사용 가능, public routes에서는 undefined)
  const [authToken, setAuthToken] = useState<string | undefined>()
  useEffect(() => {
    getAuthToken().then(setAuthToken)
  }, [])

  // Backend Realtime API 훅 사용
  const {
    isConnected: isBackendRealtimeConnected,
    connect: connectBackendRealtime,
    disconnect: disconnectBackendRealtime,
    sendMessage: sendBackendRealtimeMessage,
    setSearchEnabled: setBackendSearchEnabled,
  } = useSearchChat({
    agentId,
    avatarId,
    autoConnect: false, // 수동 연결
    token: authToken,
  })

  // 채팅 모드에서 검색 토글 표시 여부
  const showSearchToggle = chatModeController.currentMode === 'chat'

  // Backend Realtime 연결 관리
  // chatBackend가 'backend_realtime'이고 채팅 모드일 때 연결
  useEffect(() => {
    if (chatBackend === 'backend_realtime' && chatModeController.currentMode === 'chat') {
      if (!isBackendRealtimeConnected) {
        console.log('🔌 [SEARCH-CHAT] Backend Realtime API 연결 시작...')
        connectBackendRealtime()
      }
    } else {
      // 음성 모드거나 voice_server 백엔드면 연결 해제
      if (isBackendRealtimeConnected) {
        console.log('🔌 [SEARCH-CHAT] Backend Realtime API 연결 해제...')
        disconnectBackendRealtime()
      }
    }
  }, [chatBackend, chatModeController.currentMode, isBackendRealtimeConnected, connectBackendRealtime, disconnectBackendRealtime])

  // ==================== 텍스트 모드 keepalive ====================
  // 텍스트 모드에서는 HTTP streaming으로 백엔드 직접 호출하므로
  // voice_server WebSocket에 메시지가 안 감 → 비활성 타임아웃 발생
  // → 주기적 ping으로 연결 유지 (음성↔텍스트 모드 전환 UX 보장)
  useEffect(() => {
    if (chatModeController.currentMode !== 'chat' || !isConnected) return

    const interval = setInterval(() => {
      const api = AgentConnectionManager.getAPI()
      if (api?.isConnected) {
        api.send({ type: 'ping' })
      }
    }, 15000)

    return () => clearInterval(interval)
  }, [chatModeController.currentMode, isConnected])

  // 검색 토글 핸들러
  const handleSearchToggle = useCallback((enabled: boolean) => {
    console.log('🔍 [SEARCH-TOGGLE] 웹 검색:', enabled ? 'ON' : 'OFF')

    // Zustand Store 업데이트
    useRealtimeAgentStore.getState().setSearchEnabled(enabled)

    // Backend Realtime API에도 설정 전달
    if (isBackendRealtimeConnected) {
      setBackendSearchEnabled(enabled)
    }

    // 검색 활성화 시 chatBackend를 backend_realtime으로 변경
    if (enabled && chatBackend !== 'backend_realtime') {
      setChatBackend('backend_realtime')
    }
  }, [isBackendRealtimeConnected, setBackendSearchEnabled, chatBackend, setChatBackend])

  // ==================== 채팅 관련 ====================
  const {
    messages: textChatMessages,
    isLoading: isTextChatLoading,
    sendMessage: sendTextChatMessage,
    sendGreeting: sendTextChatGreeting
  } = useTextChat()
  // 인사 함수를 ref에 저장 (모드별 분기)
  // 채팅 모드: 백엔드 HTTP 직접 호출 (음성 없음, 텍스트만)
  // 음성 모드: voice_server WebSocket 경유 (음성 + 텍스트)
  sendGreetingRef.current = () => {
    if (chatModeController.currentMode === 'chat') {
      sendTextChatGreeting('자기소개 해주세요')
    } else {
      const success = sendRealtimeMessage({
        mode: 'text',
        data: '자기소개 해주세요',
        language: currentLanguage,
        turnId: `greeting_${generateUUID()}`,
      })
      useRealtimeAgentStore.getState().setGreetingLoading(success)
    }
  }

  // ==================== Greeting (useGreeting 패턴) ====================
  // 동의 완료(음성 모드) + 아바타 준비 + WebSocket 연결 + avatar_setup 완료 시 인사 메시지 전송
  // 참고: 텍스트 모드 인사말은 onConsentDeclined → sendGreetingRef.current() 경로로 처리됨
  //       (declineConsent는 hasConsented를 true로 설정하지 않으므로 이 useEffect는 음성 모드 전용)
  // 인사말 전송: 3D 로딩(isAvatarReady)을 기다리지 않고 병렬로 전송해 첫 음성 지연을 줄인다.
  // (서버 TTS 왕복을 3D 로딩과 겹치게 → 첫 음성 = max(로딩, TTS))
  useEffect(() => {
    if (
      hasConsented &&
      isConnected &&
      isAvatarSetupComplete &&
      !hasPlayedGreetingRef.current
    ) {
      hasPlayedGreetingRef.current = true

      const success = sendRealtimeMessage({
        mode: 'text',
        data: '자기소개 해주세요',
        language: currentLanguage,
        turnId: `greeting_${generateUUID()}`,
      })

      useRealtimeAgentStore.getState().setGreetingLoading(success)

      if (!success) {
        hasPlayedGreetingRef.current = false
      }
    }
  }, [hasConsented, isConnected, isAvatarSetupComplete, sendRealtimeMessage, currentLanguage])

  // entrance 애니메이션: 아바타가 실제로 준비된 뒤 재생 (인사말 요청과 분리)
  useEffect(() => {
    if (shouldPreplayGreetingEntrance(
      isAvatarReady,
      hasPlayedGreetingRef.current,
      isGreetingLoading,
    )) {
      useAvatarStore.getState().setCurrentAnimation('entrance')
    }
  }, [isAvatarReady, isGreetingLoading])

  // 동의 상태 리셋 시 greeting 상태 초기화
  useEffect(() => {
    if (!hasConsented) {
      hasPlayedGreetingRef.current = false
      useRealtimeAgentStore.getState().setGreetingLoading(false)
      console.log('🎭 [GREETING-RESET] 동의 리셋 - greeting 상태 초기화')
    }
  }, [hasConsented])

  const {
    messages: realtimeMessages,
    isLoading: isRealtimeLoading,
    isAudioPlaying
  } = useRealtimeAgentStore(
    useShallow(state => ({
      messages: state.messages,
      isLoading: state.isLoading,
      isAudioPlaying: state.isAudioPlaying
    }))
  )

  // 현재 모드에 맞는 메시지와 로딩 상태
  const messages = useOptimizedMessages(
    chatModeController.currentMode,
    textChatMessages,
    realtimeMessages
  )
  const isLoading = useOptimizedLoading(
    chatModeController.currentMode,
    isTextChatLoading,
    isRealtimeLoading
  )

  // ==================== 부가 기능 Hooks ====================
  // Agent 데이터 로깅
  useAgentDataLogger({
    agentData: agentData || null,
    agentId,
  })

  // 아바타 로딩 관리
  useAvatarLoadingManager({
    onAvatarLoaded: () => {
      // Avatar Store의 completeLoading이 Realtime Agent Store를 업데이트함
    },
  })

  // VAD 상태 동기화
  // speaking 상태: currentAnimation이 'speaking'이면 VAD를 pause (TTS 재생 중)
  const isSpeaking = currentAnimation === 'speaking'
  useVADSynchronizer({
    vadController,
    isConsentVisible: consentController.isConsentModalVisible,
    currentChatMode: chatModeController.currentMode,
    isMicOn: mediaController.isMicOn,
    isLoading,
    isSpeaking,
    isAudioPlaying,
  })

  // 메시지 핸들러
  const { handleSendMessage } = useMessageHandler({
    currentChatMode: chatModeController.currentMode,
    isAudioPlaying,
    sendTextChatMessage,
    sendRealtimeMessage,
    pauseAudio,
    // Backend Realtime API (웹 검색 지원)
    sendBackendRealtimeMessage,
  })

  // ==================== 핸들러 함수 ====================
  const handleChatModeToggle = useCallback(() => {
    if (chatModeController.currentMode === 'voice') {
      // 채팅 모드로 전환 시 현재 재생 중인 오디오 정지
      pauseAudio()
      chatModeController.switchToChatMode()
      console.log('⌨️ [CHAT_MODE_TOGGLE] 채팅 모드 전환 - 오디오 정지')
    } else {
      chatModeController.switchToVoiceMode()
      console.log('🎤 [CHAT_MODE_TOGGLE] 음성 모드 전환')
    }
  }, [chatModeController, pauseAudio])

  const handleNavigateToMode = async (mode: "call" | "text" | null) => {
    console.log('🧭 Navigate to mode:', mode)
    if (mode === null) {
      // 로고 클릭 시 전체 리셋 → ConsentModal로 돌아가기
      console.log('🔄 [NAVIGATE] 로고 클릭 - 전체 상태 리셋')

      // 0. 세션 메시지 DB 저장 (Analytics용) - 비동기로 실행
      flushSessionToDb()

      // 1. 오디오 일시정지
      pauseAudio()

      // 2. 현재 턴 정리
      useRealtimeAgentStore.getState().clearCurrentTurn()

      // 3. 메시지 초기화
      useRealtimeAgentStore.getState().setMessages([])

      // 4. Greeting ref 리셋 (다음 진입 시 인사말 재생을 위해)
      hasPlayedGreetingRef.current = false

      // 5. 동의 상태 리셋 (ConsentModal 다시 표시)
      consentController.resetConsent()

      // 6. 채팅 모드 초기화 (기본 voice 모드로)
      chatModeController.switchToVoiceMode()

      console.log('✅ [NAVIGATE] 상태 리셋 완료 - ConsentModal 표시')
    } else if (mode === "text") {
      chatModeController.switchToChatMode()
    } else if (mode === "call") {
      chatModeController.switchToVoiceMode()
    }
  }

  const handleBackClick = useCallback(() => {
    console.log('🔙 뒤로 가기 - 전체 상태 초기화')

    // 0. 세션 메시지 DB 저장 (Analytics용) - 비동기로 실행
    flushSessionToDb()

    // 1. 오디오 일시정지
    pauseAudio()

    // 2. 현재 턴 정리 (재생 중인 내용 중단)
    useRealtimeAgentStore.getState().clearCurrentTurn()
    console.log('🔙 [BACK] clearCurrentTurn 완료')

    // 3. 메시지 초기화
    useRealtimeAgentStore.getState().setMessages([])
    console.log('🔙 [BACK] setMessages([]) 완료')

    // 4. Greeting ref 리셋 (다음 진입 시 인사말 재생을 위해)
    hasPlayedGreetingRef.current = false
    console.log('🔙 [BACK] hasPlayedGreetingRef 리셋 완료')

    // 5. 아바타가 이미 로드되어 있는 경우 로딩 상태 false로 설정
    const isAvatarReady = useAvatarStore.getState().isAvatarReady
    if (isAvatarReady) {
      useRealtimeAgentStore.getState().setAvatarLoading(false)
      console.log('🔙 [BACK] 아바타 이미 로드됨 - isAvatarLoading: false')
    }

    console.log('✅ 상태 초기화 완료 - 재진입 준비됨')
  }, [flushSessionToDb, pauseAudio])

  // ==================== UI에 전달할 Props ====================
  return {
    // Config
    mergedConfig,

    // Controllers
    chatModeController,
    consentController,
    mediaController,
    vadController,

    // Messages & Loading
    messages,
    isLoading,

    // Voice Chat
    pitch,
    pauseAudio,

    // Handlers
    handleSendMessage,
    handleChatModeToggle,
    handleNavigateToMode,
    handleBackClick,

    // Agent Data
    agentData,
    isAgentLoading,
    avatarType,

    // Custom Avatar
    customAvatar,
    isCustomAvatarLoaded: effectiveIsCustomAvatarLoaded,

    // Reconnect Modal
    showReconnectModal,
    disconnectReason,
    handleReconnect,

    // Search Chat (Backend Realtime API)
    isSearchEnabled,
    searchState,
    searchQuery,
    handleSearchToggle,
    showSearchToggle,

    // Idle Timer (웹 모드용)
    isTimerVisible: idleTimer.isTimerVisible,
    remainingSeconds: idleTimer.remainingSeconds,
    resetIdleTimer: idleTimer.resetTimer,
  }
}
